import type { CarImage } from "@/lib/listings-api";

export interface LocalImage extends CarImage {
  uploading?: boolean;
  previewUrl?: string;
  /**
   * Task #6708 — the upload failed. The tile stays in the grid (wizard
   * only, via `keepFailedUploads`) with an explicit error overlay and a
   * retry button instead of silently disappearing. `retryBlob` keeps the
   * processed image bytes so retry doesn't need the user to re-pick the
   * file. Failed tiles never count toward photo requirements and are
   * excluded from reorder persistence.
   */
  failed?: boolean;
  retryBlob?: Blob;
}

export type EditorState =
  | {
      imageSource: string;
      mode: "new";
      file: File;
      orderIndex: number;
      shouldBePrimary: boolean;
    }
  | {
      imageSource: string;
      mode: "edit";
      photoId: number;
      orderIndex: number;
      shouldBePrimary: boolean;
    };

/**
 * Cheap client-side quality heuristic shared by the listing wizard
 * (StepPhotos) and the edit gallery (EditPhotosGallery). Renders the
 * source into a 100×100 canvas and inspects average brightness, pixel
 * dimensions and a Laplacian-variance sharpness proxy. No re-encode of
 * the upload blob happens here — this is purely a gate.
 */
export function checkImageQuality(
  file: File,
): Promise<{ dark: boolean; small: boolean; blurry: boolean }> {
  return new Promise(resolve => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const size = 100;
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");
      if (!ctx) { resolve({ dark: false, small: false, blurry: false }); return; }
      // Audit fix (May 2026): white-fill before drawImage. A
      // transparent PNG drawn onto an unfilled canvas yields zero
      // brightness for the transparent regions, which used to make
      // the `dark` heuristic fire a false "Image is too dark"
      // rejection on legitimate photos with any transparent areas.
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, size, size);
      ctx.drawImage(img, 0, 0, size, size);
      const imageData = ctx.getImageData(0, 0, size, size);
      const pixels = imageData.data;
      let totalBrightness = 0;
      const grayscale: number[] = [];
      for (let i = 0; i < pixels.length; i += 4) {
        const gray = (pixels[i] + pixels[i + 1] + pixels[i + 2]) / 3;
        totalBrightness += gray;
        grayscale.push(gray);
      }
      const pixelCount = size * size;
      const avgBrightness = totalBrightness / pixelCount;

      let varianceSum = 0;
      for (let y = 1; y < size - 1; y++) {
        for (let x = 1; x < size - 1; x++) {
          const idx = y * size + x;
          const laplacian =
            -4 * grayscale[idx] +
            grayscale[idx - 1] + grayscale[idx + 1] +
            grayscale[idx - size] + grayscale[idx + size];
          varianceSum += laplacian * laplacian;
        }
      }
      const laplacianVariance = varianceSum / ((size - 2) * (size - 2));

      // The editor exports every crop at a fixed 16:9 aspect ratio
      // (see FIXED_ASPECT_RATIO in ImageEditorModal). The old
      // `width < 800 || height < 600` floor was incompatible with that
      // output: a 16:9 image only reaches 600px tall at ~1067px wide,
      // and an 800px-wide 16:9 image is just 450px tall — so any
      // moderately tight (but perfectly valid) landscape crop was
      // rejected as "resolution too low". Gate instead on the long and
      // short edges of the frame so a valid 16:9 crop can actually pass,
      // while still rejecting genuinely tiny images. 800×450 is the
      // smallest allowed 16:9 frame.
      const longEdge = Math.max(img.width, img.height);
      const shortEdge = Math.min(img.width, img.height);

      resolve({
        dark: avgBrightness < 50,
        small: longEdge < 800 || shortEdge < 450,
        blurry: laplacianVariance < 100,
      });
    };
    img.onerror = () => resolve({ dark: false, small: false, blurry: false });
    img.src = URL.createObjectURL(file);
  });
}
